import { jsPDF } from 'jspdf';

const MARGIN_X = 18;
const PAGE_WIDTH = 210; // A4 mm
const IMAGE_WIDTH = 175;
const LINE = 4;
const TEXT_SIZE = 10;
const BULLET_SIZE = 20;
const BULLET_LINE_HEIGHT = 3;
const PT_TO_MM = 0.352778;

export function formatDate(raw) {
  if (!raw) return 'Not Set';
  const datePart = String(raw).split('T')[0];
  const [year, month, day] = datePart.split('-').map(Number);
  if (!year || !month || !day) return 'Not Set';
  const mm = String(month).padStart(2, '0');
  const dd = String(day).padStart(2, '0');
  return `${mm}/${dd}/${year}`;
}

function addBulletItem(doc, y, text) {
  const textX = MARGIN_X + 10;
  const maxWidth = PAGE_WIDTH - textX - 10;

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(TEXT_SIZE);
  const lines = doc.splitTextToSize(text, maxWidth);
  const firstLineBaseline = y + BULLET_LINE_HEIGHT;
  const bulletBaseline =
    firstLineBaseline + 0.35 * (BULLET_SIZE - TEXT_SIZE) * PT_TO_MM;

  doc.setFontSize(BULLET_SIZE);
  doc.text('\u2022', MARGIN_X + 7, bulletBaseline, { align: 'right' });
  doc.setFontSize(TEXT_SIZE);
  doc.text(lines, textX, firstLineBaseline);

  return y + lines.length * BULLET_LINE_HEIGHT + 1;
}

/**
 * Builds the OxR confirmation letter PDF document.
 * Caller is responsible for saving or displaying the returned jsPDF instance.
 */
export function buildOxrConfirmationPdf({ username, accountNumber, effectiveDate, headerDataUrl, headerDims }) {
  const imageHeight = IMAGE_WIDTH * (headerDims.height / headerDims.width);
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });

  let y = 10;
  const centerX = (PAGE_WIDTH - IMAGE_WIDTH) / 2;
  doc.addImage(headerDataUrl, 'PNG', centerX, y, IMAGE_WIDTH, imageHeight);
  y = y + 25;

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(TEXT_SIZE);
  doc.text(`Dear ${username},`, MARGIN_X, y + 4);
  y += 10;

  const intro =
    'Thank you for purchasing the Overwatch x Rescue Emergency Assistance Plan. This letter serves as confirmation of your active coverage and rescue benefits.';
  const introLines = doc.splitTextToSize(intro, PAGE_WIDTH - MARGIN_X * 2);
  doc.text(introLines, MARGIN_X, y);
  y += introLines.length * LINE + 2;

  doc.setFont('helvetica', 'bold');
  doc.text('Plan Details', MARGIN_X, y + 4);
  y += 7;
  doc.setFont('helvetica', 'normal');

  y = addBulletItem(doc, y, 'Plan Name: Overwatch x Rescue DTC Emergency Assistance Plan');
  y = addBulletItem(doc, y, `Name on Plan: ${username}`);
  y = addBulletItem(doc, y, `Account Number: ${accountNumber}`);
  y = addBulletItem(doc, y, `Effective Date: ${effectiveDate}`);
  y = addBulletItem(doc, y, 'Plan Duration: 365 days');

  doc.setFont('helvetica', 'bold');
  doc.text('Coverage Benefits', MARGIN_X, y + 4);
  y += 7;
  doc.setFont('helvetica', 'normal');
  doc.text('Your Overwatch x Rescue plan includes:', MARGIN_X, y + 4);
  y += 7;

  y = addBulletItem(doc, y, '24/7 multilingual emergency SOS assistance');
  y = addBulletItem(doc, y, 'Coordinated search and rescue (SAR) services');
  y = addBulletItem(doc, y, 'No-cost point of incident medical and security evacuation');
  y = addBulletItem(doc, y, 'Response to hazardous summer and winter sports incidents');
  y = addBulletItem(doc, y, 'Crisis consultation and emergency message transmission');
  y = addBulletItem(doc, y, 'Access to a global network of SAR agencies and resources');
  y = addBulletItem(
    doc,
    y,
    'Includes activities such as paragliding, mountaineering to heights above 3,000 meters, and rock climbing.'
  );

  doc.setFont('helvetica', 'bold');
  doc.text('Important Information:', MARGIN_X, y + 4);
  y += 7;
  doc.setFont('helvetica', 'normal');

  y = addBulletItem(doc, y, 'In case of emergency, activate the SOS feature on your connected device');
  y = addBulletItem(doc, y, 'For any questions or concerns, please contact us at: information@wwfocus.com');
  y += 4;

  const note1 =
    'Please note that all assistance is provided at the discretion of FocusPoint International and in accordance with the full terms and conditions of the Overwatch x Rescue plan.';
  const note1Lines = doc.splitTextToSize(note1, PAGE_WIDTH - MARGIN_X * 2);
  doc.text(note1Lines, MARGIN_X, y);
  y += note1Lines.length * LINE + 4;

  const note2 =
    'We hope you never need to use these services, but rest assured that should an emergency arise, Overwatch x Rescue is here to provide you with comprehensive assistance and peace of mind.';
  const note2Lines = doc.splitTextToSize(note2, PAGE_WIDTH - MARGIN_X * 2);
  doc.text(note2Lines, MARGIN_X, y);
  y += note2Lines.length * LINE + 4;

  doc.text('Sincerely,', MARGIN_X, y);
  y += 4;
  doc.text('The Overwatch x Rescue Team', MARGIN_X, y);

  return doc;
}
